import { z } from 'zod';

export const JOURNEY_STATUSES = ['DRAFTING', 'ACTIVE', 'ARRIVED', 'EXPIRED', 'ABANDONED'] as const;
export const JourneyStatus = z.enum(JOURNEY_STATUSES);
export type JourneyStatus = z.infer<typeof JourneyStatus>;

export const JOURNEY_INTAKE_STEPS = ['ORIGIN', 'GOAL', 'DEADLINE', 'READY'] as const;
export const JourneyIntakeStep = z.enum(JOURNEY_INTAKE_STEPS);
export type JourneyIntakeStep = z.infer<typeof JourneyIntakeStep>;

export const JOURNEY_MESSAGE_ROLES = ['USER', 'GUIDE'] as const;
export const JourneyMessageRole = z.enum(JOURNEY_MESSAGE_ROLES);
export type JourneyMessageRole = z.infer<typeof JourneyMessageRole>;

export const JOURNEY_STEP_STATUSES = ['ASSIGNED', 'DONE', 'FAILED', 'SKIPPED'] as const;
export const JourneyStepStatus = z.enum(JOURNEY_STEP_STATUSES);
export type JourneyStepStatus = z.infer<typeof JourneyStepStatus>;

/** Кап длины реплики стартового диалога. */
export const JOURNEY_INTAKE_TEXT_MAX = 1000;
/** Кап длины дневного отчёта — длиннее не нужен, а LLM-контекст дорог. */
export const JOURNEY_REPORT_TEXT_MAX = 2000;
/** Границы числа этапов плана; нижняя применяется только когда дней до дедлайна >= 3. */
export const JOURNEY_STAGES_MIN = 3;
export const JOURNEY_STAGES_MAX = 8;
/** Горизонт дедлайна: дальше года цель перестаёт быть игрой. */
export const JOURNEY_DEADLINE_MAX_DAYS = 365;

/**
 * Машиночитаемые коды ошибок игры. Сервер кладёт их в `message` вместо
 * русского текста: у mini-app две локали, серверная фраза непригодна.
 * Единственное исключение — 422 у `POST /me/journey/arrive`, где в `message`
 * идёт `reply` модели (объяснение, почему финиш не засчитан) на языке игрока.
 */
export const JOURNEY_ERROR_CODES = {
  textInvalid: 'text_invalid',
  journeyAlreadyActive: 'journey_already_active',
  intakeCapReached: 'intake_cap_reached',
  intakeSettled: 'intake_settled',
  intakeDeadlineExpired: 'intake_deadline_expired',
  journeyMissing: 'journey_missing',
  intakeUnfinished: 'intake_unfinished',
  draftIncomplete: 'draft_incomplete',
  deadlineTooSoon: 'deadline_too_soon',
  deadlineTooFar: 'deadline_too_far',
  journeyNotActive: 'journey_not_active',
  planEmpty: 'plan_empty',
  stepNotTaken: 'step_not_taken',
  stepAlreadyReported: 'step_already_reported',
  finishedJourneyMissing: 'finished_journey_missing',
  openJourneyMissing: 'open_journey_missing',
  startsCapReached: 'starts_cap_reached',
  arrivalCapReached: 'arrival_cap_reached',
  journeyConflict: 'journey_conflict',
  llmMissing: 'llm_missing',
  intakeFailed: 'intake_failed',
  planFailed: 'plan_failed',
  stepFailed: 'step_failed',
  reportFailed: 'report_failed',
  arriveFailed: 'arrive_failed',
  summaryFailed: 'summary_failed',
  measureInvalid: 'measure_invalid',
  metricMissing: 'metric_missing',
  stepVariantMissing: 'step_variant_missing',
  swapCapReached: 'swap_cap_reached',
  messageCapReached: 'message_cap_reached',
  offerFailed: 'offer_failed',
  offerCapReached: 'offer_cap_reached',
  offerNotReady: 'offer_not_ready',
} as const;
export type JourneyErrorCode = (typeof JOURNEY_ERROR_CODES)[keyof typeof JOURNEY_ERROR_CODES];

/**
 * Дефолты капов дублируют `EnvSchema`, чтобы клиент мог предупреждать заранее,
 * а не показывать отказ постфактум. Фактическое значение живёт в env — сервер
 * считает от него и отдаёт остаток в `JourneyIntakeDto.messagesLeft`.
 */
export const JOURNEY_INTAKE_MAX_MESSAGES_DEFAULT = 30;
/**
 * Кап уточнений на шаге ORIGIN — первый шаг, с которого начинается диалог.
 * С 2026-09-24 — 1: интейк 3–5 реплик. С 2026-10-02 — 2: при одном вопросе ось
 * времени не собиралась ни в одном из 8 живых путей, и `TIME_BUDGET` не работал.
 */
export const JOURNEY_INTAKE_ORIGIN_MAX_FOLLOWUPS_DEFAULT = 2;
export const JOURNEY_MAX_STARTS_PER_DAY_DEFAULT = 3;

/**
 * Round-trip через `Date`: regex проверяет только форму (4-2-2 цифры) —
 * `2026-13-99` или `2026-02-30` её проходят. Без второй проверки такая строка
 * доезжает до сервиса как валидный YYYY-MM-DD, там становится `new Date(...)`
 * (Invalid Date либо тихо перекатившаяся дата — `2026-02-30` → `2026-03-02`),
 * и Prisma бросает `PrismaClientValidationError` синхронно — в `apps/api` нет
 * глобального exception filter, поэтому дефолтный фильтр Nest превращает это
 * в 500. Если распарсенная дата не даёт обратно ту же строку, это не
 * настоящий календарный день.
 */
const isCalendarDate = (value: string): boolean => {
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};
const CalendarDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(isCalendarDate, { message: 'invalid calendar date' });

/**
 * Query `GET /me/journey/steps`. Курсор — `gameDate` последнего элемента страницы.
 */
export const JourneyStepsQuery = z.object({
  cursor: CalendarDate.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export type JourneyStepsQuery = z.infer<typeof JourneyStepsQuery>;

/** Направление метрики цели: «вверх» — подписчики, «вниз» — вес. */
export const JOURNEY_METRIC_DIRECTIONS = ['up', 'down'] as const;
export const JourneyMetricDirection = z.enum(JOURNEY_METRIC_DIRECTIONS);
export type JourneyMetricDirection = z.infer<typeof JourneyMetricDirection>;

/** Верхняя граница замера — защита колонки Decimal(14,2), а не предметное ограничение. */
export const JOURNEY_MEASURE_MAX = 1e9;

export const JourneyMetricDto = z.object({
  label: z.string(),
  unit: z.string(),
  direction: JourneyMetricDirection,
  /** `null` — стартовое значение ещё не названо; его задаст первый замер. */
  start: z.number().nullable(),
  target: z.number(),
  /** Последний замер, иначе `start`. */
  current: z.number().nullable(),
  /** Доля пути от `start` к `target`, 0..1; `null`, пока `start` неизвестен. */
  progress: z.number().min(0).max(1).nullable(),
});
export type JourneyMetricDto = z.infer<typeof JourneyMetricDto>;

export const JOURNEY_STEP_VARIANT_KINDS = ['short', 'full'] as const;
export const JourneyStepVariantKind = z.enum(JOURNEY_STEP_VARIANT_KINDS);
export type JourneyStepVariantKind = z.infer<typeof JourneyStepVariantKind>;
export const JourneyStepVariantDto = z.object({ kind: JourneyStepVariantKind, text: z.string() });
export type JourneyStepVariantDto = z.infer<typeof JourneyStepVariantDto>;

export const JOURNEY_REPORT_MARKS = ['done', 'partial', 'missed'] as const;
export const JourneyReportMark = z.enum(JOURNEY_REPORT_MARKS);
export type JourneyReportMark = z.infer<typeof JourneyReportMark>;

/** Причина замены — enum, не текст: у промпта задания нет safety-контура. */
export const JOURNEY_SWAP_REASONS = ['too_big', 'not_relevant', 'cant_today'] as const;
export const JourneySwapReason = z.enum(JOURNEY_SWAP_REASONS);
export type JourneySwapReason = z.infer<typeof JourneySwapReason>;

export const JourneyDto = z.object({
  id: z.uuid(),
  status: JourneyStatus,
  originText: z.string().nullable(),
  goalText: z.string().nullable(),
  successCriteria: z.string().nullable(),
  /**
   * Исходная цель, если ведущий урезал её под срок: `goalText`/`successCriteria`
   * тогда несут достижимую веху, а эти два поля — то, что человек попросил
   * изначально. `null` у обоих = цель не урезали. Всегда заполняются парой.
   */
  ultimateGoalText: z.string().nullable(),
  ultimateSuccessCriteria: z.string().nullable(),
  /** 'YYYY-MM-DD' — игровой день дедлайна в таймзоне, снятой на старте. */
  deadlineDate: z.string().nullable(),
  daysLeft: z.number().int().min(0).nullable(),
  stagesTotal: z.number().int().min(0),
  stageIndex: z.number().int().min(0).nullable(),
  stageDaysLeft: z.number().int().min(0).nullable(),
  /**
   * Сколько заданий засчитано за весь путь. Вехи на карте — этапы по 3–11 дней,
   * они стоят неделями; это единственное число, которое двигается после каждого
   * закрытого дня, иначе выполненное задание не меняет на экране ничего.
   */
  stepsDone: z.number().int().min(0),
  /**
   * Действия плана — шкала прогресса мельче этапов (2026-09-24): этапов 4–6,
   * а действие закрывается раз в день-два. Закрытый этап считается целиком,
   * даже если судья закрыл его раньше последнего действия.
   */
  actionsTotal: z.number().int().min(0),
  actionsDone: z.number().int().min(0),
  /** Действия текущего этапа; `null`, пока текущего этапа нет. */
  stageActionsTotal: z.number().int().min(0).nullable(),
  stageActionsDone: z.number().int().min(0).nullable(),
  behindSchedule: z.boolean(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  summaryText: z.string().nullable(),
  /** Путь остановлен контуром безопасности (кризисная реплика/отчёт), а не завершён игрой. */
  haltedForSafety: z.boolean(),
  /** Метрика цели; `null` — цель без числа (финиш по заявке, как раньше). */
  metric: JourneyMetricDto.nullable(),
  /** Пора спросить «сколько сейчас?»: метрика есть, последнему замеру ≥ 7 игровых дней. */
  measureDue: z.boolean(),
});
export type JourneyDto = z.infer<typeof JourneyDto>;

export const JourneyStepDto = z.object({
  id: z.uuid(),
  gameDate: z.string(),
  text: z.string(),
  status: JourneyStepStatus,
  reportText: z.string().nullable(),
  verdict: z.string().nullable(),
  /** Варианты задания; пустой массив — легаси-шаг с одним `text`. */
  variants: z.array(JourneyStepVariantDto),
  chosenKind: JourneyStepVariantKind.nullable(),
  swapsLeft: z.number().int().min(0),
  reportMark: JourneyReportMark.nullable(),
});
export type JourneyStepDto = z.infer<typeof JourneyStepDto>;

export const JourneyStepsPageDto = z.object({
  items: z.array(JourneyStepDto),
  nextCursor: z.string().nullable(),
});
export type JourneyStepsPageDto = z.infer<typeof JourneyStepsPageDto>;

export const JourneyIntakeMessageDto = z.object({
  role: JourneyMessageRole,
  content: z.string(),
  createdAt: z.iso.datetime(),
});
export type JourneyIntakeMessageDto = z.infer<typeof JourneyIntakeMessageDto>;

export const JourneyIntakeDraftDto = z.object({
  originText: z.string().nullable(),
  goalText: z.string().nullable(),
  successCriteria: z.string().nullable(),
  deadlineDate: z.string().nullable(),
  metric: JourneyMetricDto.nullable(),
});
export type JourneyIntakeDraftDto = z.infer<typeof JourneyIntakeDraftDto>;

export const JourneyIntakeDto = z.object({
  step: JourneyIntakeStep,
  settled: z.boolean(),
  /** Сколько реплик игрок ещё может отправить до `JOURNEY_INTAKE_MAX_MESSAGES`. */
  messagesLeft: z.number().int().min(0),
  /**
   * Сколько уточняющих вопросов ведущему осталось на ТЕКУЩЕМ шаге. 0 — следующая
   * реплика шаг закрывает. Показывается игроку: молчаливый обрыв разговора на
   * исчерпании капа читался как «меня не дослушали».
   */
  followupsLeft: z.number().int().min(0),
  messages: z.array(JourneyIntakeMessageDto),
  draft: JourneyIntakeDraftDto,
});
export type JourneyIntakeDto = z.infer<typeof JourneyIntakeDto>;

export const JourneyStateDto = z.object({
  journey: JourneyDto.nullable(),
  intake: JourneyIntakeDto.nullable(),
  today: JourneyStepDto.nullable(),
});
export type JourneyStateDto = z.infer<typeof JourneyStateDto>;

export const SendJourneyIntakeInput = z.object({
  text: z.string().trim().min(1).max(JOURNEY_INTAKE_TEXT_MAX),
  /** Срок, выбранный кнопкой; учитывается только на шаге DEADLINE. */
  deadlineDate: CalendarDate.optional(),
});
export type SendJourneyIntakeInput = z.infer<typeof SendJourneyIntakeInput>;

/** Легаси-форма отчёта: `{text}` — прежний судья (бот и старый mini-app). */
export const ReportJourneyStepTextInput = z.object({
  text: z.string().trim().min(1).max(JOURNEY_REPORT_TEXT_MAX),
});
export type ReportJourneyStepTextInput = z.infer<typeof ReportJourneyStepTextInput>;

const MarkReportInput = z.object({
  mark: JourneyReportMark,
  text: z
    .string()
    .trim()
    .max(JOURNEY_REPORT_TEXT_MAX)
    .optional()
    .transform((v) => (v ? v : undefined)),
});
/**
 * Отчёт: `{mark, text?}` — кнопкой (новый клиент), `{text}` — прежний судья
 * (бот и старый mini-app). Порядок в union важен: MarkReportInput первым.
 */
export const ReportJourneyStepInput = z.union([MarkReportInput, ReportJourneyStepTextInput]);
export type ReportJourneyStepInput = z.infer<typeof ReportJourneyStepInput>;

/**
 * HTTP-тело `POST /me/journey/step/report` — одна объектная схема вместо
 * union: `createZodDto` не умеет class-extends на union (TS2509). Либо
 * отметка (текст к ней необязателен), либо непустой текст (легаси-судья).
 * Движок в `reportMark` всё равно валидирует union `ReportJourneyStepInput`.
 */
export const ReportJourneyStepBody = z
  .object({
    mark: JourneyReportMark.optional(),
    text: z.string().trim().max(JOURNEY_REPORT_TEXT_MAX).optional(),
  })
  .refine((b) => b.mark !== undefined || (b.text?.length ?? 0) > 0, {
    message: 'нужна отметка или непустой текст отчёта',
  });
export type ReportJourneyStepBody = z.infer<typeof ReportJourneyStepBody>;

export const MeasureJourneyInput = z.object({
  value: z.number().finite().min(0).max(JOURNEY_MEASURE_MAX),
});
export type MeasureJourneyInput = z.infer<typeof MeasureJourneyInput>;
export const ChooseJourneyStepInput = z.object({ kind: JourneyStepVariantKind });
export type ChooseJourneyStepInput = z.infer<typeof ChooseJourneyStepInput>;
export const SwapJourneyStepInput = z.object({ reason: JourneySwapReason });
export type SwapJourneyStepInput = z.infer<typeof SwapJourneyStepInput>;

/** Свободное сообщение днём (спека 2026-10-02 §6): вопрос, просьба о замене или отчёт словами. */
export const JOURNEY_MESSAGE_INTENTS = ['question', 'change', 'report'] as const;
export const JourneyMessageIntent = z.enum(JOURNEY_MESSAGE_INTENTS);
export type JourneyMessageIntent = z.infer<typeof JourneyMessageIntent>;
export const SendJourneyStepMessageInput = z.object({
  text: z.string().trim().min(1).max(JOURNEY_REPORT_TEXT_MAX),
});
export type SendJourneyStepMessageInput = z.infer<typeof SendJourneyStepMessageInput>;
export type JourneyMessageResultDto = JourneyStateDto & { messageIntent: JourneyMessageIntent };

export const ClaimArrivalInput = z.object({
  text: z.string().trim().min(1).max(JOURNEY_REPORT_TEXT_MAX),
});
export type ClaimArrivalInput = z.infer<typeof ClaimArrivalInput>;

/** Откуда человек вошёл в воронку: быстрый старт мимо разбора или бесплатный разбор. */
export const JOURNEY_OFFER_ENTRIES = ['QUICK', 'FREE'] as const;
export const JourneyOfferEntry = z.enum(JOURNEY_OFFER_ENTRIES);
export type JourneyOfferEntry = z.infer<typeof JourneyOfferEntry>;

export const JOURNEY_OFFER_STATUSES = ['OPEN', 'ANALYZED', 'ZERO_TASK', 'GRADED', 'PAID', 'HALTED'] as const;
export const JourneyOfferStatus = z.enum(JOURNEY_OFFER_STATUSES);
export type JourneyOfferStatus = z.infer<typeof JourneyOfferStatus>;

export const JOURNEY_OFFER_GRADES = ['STRONG', 'WEAK'] as const;
export const JourneyOfferGrade = z.enum(JOURNEY_OFFER_GRADES);
export type JourneyOfferGrade = z.infer<typeof JourneyOfferGrade>;

/** Полная цена заглушки и цена после сильного нулевого задания. */
export const JOURNEY_OFFER_PRICE_CENTS = 1000;
export const JOURNEY_OFFER_DISCOUNT_CENTS = 500;

export const JourneyOfferDto = z.object({
  entry: JourneyOfferEntry,
  status: JourneyOfferStatus,
  pointA: z.string().nullable(),
  pointB: z.string().nullable(),
  analysis: z.string().nullable(),
  zeroTask: z.string().nullable(),
  zeroAnswer: z.string().nullable(),
  grade: JourneyOfferGrade.nullable(),
  priceCents: z.number().int().nullable(),
  paidAt: z.string().nullable(),
  accessUntil: z.string().nullable(),
});
export type JourneyOfferDto = z.infer<typeof JourneyOfferDto>;

export const JourneyOfferStateDto = z.object({
  offer: JourneyOfferDto.nullable(),
});
export type JourneyOfferStateDto = z.infer<typeof JourneyOfferStateDto>;

export const AnalyzeJourneyOfferInput = z.object({
  pointA: z.string().trim().min(1).max(JOURNEY_INTAKE_TEXT_MAX),
  pointB: z.string().trim().min(1).max(JOURNEY_INTAKE_TEXT_MAX),
});
export type AnalyzeJourneyOfferInput = z.infer<typeof AnalyzeJourneyOfferInput>;

export const GradeJourneyOfferInput = z.object({
  text: z.string().trim().min(1).max(JOURNEY_REPORT_TEXT_MAX),
});
export type GradeJourneyOfferInput = z.infer<typeof GradeJourneyOfferInput>;
